import { useEffect, useState } from "react";
import { Button } from "@/projects/NPS/Stepper/common/button";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import DigilockerAutorizeService from "@/services/NPS/Stepper/APIs/digilockerAutorize";
import DigilockerCallbackService from "@/services/NPS/Stepper/APIs/digilockerCallback";
import { useNavigate } from "react-router-dom";
import { navigationRoutes } from "@/utils/routes";
import { notify } from "@/components/common/Notification";
import Loader from "@/projects/NPS/Stepper/common/Loader";
interface StepKYCProps {
  kycForm: any;
  setKycForm: (data: any) => void;
  onNext: () => void;
  message: any;
  registrationDetails: Registration | null;
  setCurrent: (current: number) => void;
}

const StepKYC: React.FC<StepKYCProps> = ({
  kycForm,
  setKycForm,
  onNext,
  registrationDetails,
  setCurrent,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(kycForm);
  const [accepted, setAccepted] = useState(false);
  const [digilockerConnected, setDigilockerConnected] =
    useState<boolean>(false);
  const [errors, setErrors] = useState({
    pan: "",
    fullName: "",
    dob: "",
    mobile: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    // Only update if kycForm is different from formData to prevent unnecessary re-renders
    if (
      kycForm &&
      (kycForm?.pan !== formData?.pan ||
        kycForm?.fullName !== formData?.fullName)
    ) {
      const mappedData = {
        pan: kycForm?.pan ?? "",
        fullName: kycForm?.name_as_per_pan ?? "",
        dob: kycForm?.dob_as_per_pan ?? "",
        mobile: kycForm?.mobNumber ?? "",
        email: kycForm?.emailId ?? "",
      };
      setFormData(mappedData);
      setKycForm(mappedData); // update parent too
    }
  }, [kycForm, setKycForm, formData]);

  useEffect(() => {
    if (digilockerConnected) {
      setCurrent(1);
    }
  }, [digilockerConnected, setCurrent]);

  useEffect(() => {
    if (registrationDetails?.personalDtls.pDigilockerVerified) {
      setDigilockerConnected(
        registrationDetails?.personalDtls.pDigilockerVerified ?? false
      );
    }
  }, [registrationDetails]);

  const handleChange = (field: string, value: string) => {
    let newValue = value;
    // Convert PAN & Full Name to uppercase
    if (field === "pan" || field === "fullName") {
      newValue = value.toUpperCase();
    }

    setFormData((prev: any) => ({
      ...prev,
      [field]: newValue,
    }));

    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Call API or proceed with the registration
    console.log("KYC Data:", formData);
    onNext();
  };

  const handleDigilockerAutorize = async (e: React.FormEvent) => {
    e.preventDefault();

    let popup: Window | null = null;

    try {
      const response = await DigilockerAutorizeService.digilockerAutorize();
      const digilockerAuthUrl =
        response.auth_url ?? response.authorization_url ?? response.url;

      if (!digilockerAuthUrl) {
        throw new Error("Digilocker authorization URL not found");
      }

      const messageHandler = async (event: MessageEvent) => {
        console.log("📨 Message received:", event.origin, event.data);

        if (
          event.origin !== window.location.origin &&
          !event.origin.includes("localhost")
        ) {
          console.log("⚠️ Message from untrusted origin, ignoring");
          return;
        }

        if (event.data?.type === "DIGILOCKER_CALLBACK") {
          const { code, state } = event.data;
          console.log("✅ Authorization code received via message:", code);

          window.removeEventListener("message", messageHandler);

          if (code && state) {
            try {
              console.log("📤 Sending authorization code to backend...");
              const result = await DigilockerCallbackService.digilockerCallback(
                code ?? ""
              );
              console.log("🎉 Digilocker connected successfully!", result);
              notify.success("Digilocker verified successfully!");

              // Navigate to Step 2: Personal Details after success
              setCurrent(1); // Set the current step to 1, which corresponds to "Personal Details"
              navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
            } catch (error) {
              console.error("Failed to connect Digilocker:", error);
              notify.error("Failed to connect Digilocker. Please try again.");
            }
          }
        }
      };

      const popupWidth = 600;
      const popupHeight = 600;

      // Calculate center position
      const left = window.screenX + (window.outerWidth - popupWidth) / 2;
      const top = window.screenY + (window.outerHeight - popupHeight) / 2;

      window.addEventListener("message", messageHandler);
      popup = window.open(
        digilockerAuthUrl,
        "Digilocker_Authorization", // Avoid spaces in window name
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        console.error("Popup blocked by browser");
        window.removeEventListener("message", messageHandler);
        return;
      }
      console.log("Popup opened successfully");
    } catch (err) {
      console.error("Error during Digilocker authorization:", err);
    }

    const timer = setInterval(async () => {
      try {
        if (!popup) {
          clearInterval(timer);
          return;
        }

        if (popup.closed) {
          clearInterval(timer);
          console.log("⚠️ Popup closed by user");
          return;
        }

        const url = new URL(popup.location.href);
        console.log("🔍 Checking popup URL:", url.href);
        if (url.search.includes("error=")) {
          popup.close();
        }

        if (url.search.includes("code=")) {
          console.log(
            "Authorization code received from popup:--------------------------------------------",
            url
          );
          const params = new URLSearchParams(url.search);
          const code = params.get("code");
          const state = params.get("state");

          console.log("Authorization code received:", code);
          console.log("State parameter:", state);
          clearInterval(timer);
          popup.close();

          setIsLoading(true);
          if (code && state) {
            try {
              console.log("📤 Sending authorization code to backend...");
              await DigilockerCallbackService.digilockerCallback(code ?? "");
              console.log("🎉 Digilocker connected successfully!");

              // Set current step to "Step 2: Personal Details" after success
              setCurrent(1); // Set the current step to 1 (StepPersonalDetails)
              setIsLoading(false);
            } catch (error) {
              console.error("Failed to connect Digilocker:", error);
              notify.error("Failed to connect Digilocker. Please try again.");
              setIsLoading(false);
            } finally {
              setIsLoading(false);
            }
          }
        }
      } catch (_error) {
        if (popup?.closed) {
          clearInterval(timer);
          console.log("⚠️ Popup closed, stopping polling");
        }
      }
    }, 500);
  };

  return (
    <>
      {isLoading && <Loader message="fetching Digilocker details..." />}
      <form
        onSubmit={handleSubmit}
        className="mx-auto space-y-3 rounded-md bg-white px-0 py-6 md:space-y-5 lg:px-10 xl:px-10 2xl:px-0"
      >
        {/* Title */}
        <h2 className="font-nunito text-[20px] font-bold text-[#266985] md:text-[30px]">
          KYC Details
        </h2>

        {/* Input Fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {/* PAN */}
          <div className="flex flex-col">
            <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
              Permanent Account Number (PAN)
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              readOnly
              placeholder="Permanent Account Number (PAN)"
              value={formData?.pan}
              onChange={e => handleChange("pan", e.target.value)}
              className={`w-full cursor-not-allowed rounded-md border ${errors.pan ? "border-red-500" : "border-[#DFE7EC]"} bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none`}
            />
            {errors.pan && (
              <p className="mt-1 font-nunito text-sm font-semibold text-red-500">
                {errors.pan}
              </p>
            )}
          </div>

          {/* Full Name */}
          <div className="flex flex-col">
            <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
              Full Name (As per PAN Card)
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              readOnly
              placeholder="Full Name (As per PAN Card)"
              value={formData?.fullName}
              onChange={e => handleChange("fullName", e.target.value)}
              className={`w-full cursor-not-allowed rounded-md border ${errors.fullName ? "border-red-500" : "border-[#DFE7EC]"} bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none`}
            />
            {errors.fullName && (
              <p className="mt-1 font-nunito text-sm font-semibold text-red-500">
                {errors.fullName}
              </p>
            )}
          </div>

          {/* Date of Birth */}
          <div className="flex flex-col">
            <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
              Date of Birth (DD/MM/YYYY)
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              readOnly
              placeholder="Date of Birth (DD/MM/YYYY)"
              value={formData?.dob}
              // onFocus={e => (e.target.type = "date")} // switch to date picker on focus
              // onBlur={e => {
              //   if (!e.target.value) {
              //     e.target.type = "text"; // revert to text if empty
              //   }
              // }}
              onChange={e => handleChange("dob", e.target.value)}
              className={`w-full cursor-not-allowed rounded-md border ${errors.dob ? "border-red-500" : "border-[#DFE7EC]"} bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none`}
            />
            {errors.dob && (
              <p className="mt-1 font-nunito text-sm font-semibold text-red-500">
                {errors.dob}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-2">
          {/* Mobile Number */}
          <div className="flex flex-col">
            <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
              Mobile Number
              <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              readOnly
              placeholder="Mobile Number"
              maxLength={10}
              value={formData?.mobile}
              onChange={e =>
                handleChange("mobile", e.target.value.replace(/[^0-9]/g, ""))
              }
              className={`w-full cursor-not-allowed rounded-md border ${errors.mobile ? "border-red-500" : "border-[#DFE7EC]"} bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none`}
            />
            {errors.mobile && (
              <p className="mt-1 font-nunito text-sm font-semibold text-red-500">
                {errors.mobile}
              </p>
            )}
          </div>

          {/* Email ID */}
          <div className="flex flex-col">
            <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
              Email ID
              <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              readOnly
              placeholder="Email ID"
              value={formData?.email}
              onChange={e => handleChange("email", e.target.value)}
              className={`w-full cursor-not-allowed rounded-md border ${errors.email ? "border-red-500" : "border-[#DFE7EC]"} bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none`}
            />
            {errors.email && (
              <p className="mt-1 font-nunito text-sm font-semibold text-red-500">
                {errors.email}
              </p>
            )}
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="flex items-center gap-2 pt-5">
          <input
            type="checkbox"
            checked={accepted}
            onChange={() => setAccepted(!accepted)}
            className="h-5 w-5 rounded border-[#000] bg-[#f2f2f2] text-[#000] accent-[#000] focus:ring-0"
          />
          <label className="font-nunito text-[16px] font-semibold text-[#707070]">
            Click here to accept and proceed
          </label>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-start">
          <Button
            type="button"
            variant="secondary"
            onClick={handleDigilockerAutorize}
            className={`h-[50px] w-full rounded-md px-5 py-2 font-nunito text-[18px] font-semibold text-white md:w-fit ${
              accepted && !digilockerConnected
                ? "cursor-pointer bg-gradient-to-r from-[#127B8D] to-[#10B981]"
                : "cursor-not-allowed bg-[rgba(112,112,112,0.3)] text-[#2F363F]"
            }`}
            disabled={!accepted || digilockerConnected} // ✅ Disable until checked
          >
            Continue with Digilocker
          </Button>
        </div>
        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            variant="secondary"
            disabled={!digilockerConnected} // ✅ Disable until checked
            className={`ounded-md h-[50px] w-[180px] rounded-md py-2 font-nunito text-[18px] font-semibold tracking-[1px] text-white ${
              digilockerConnected
                ? "cursor-pointer bg-gradient-to-r from-[#127B8D] to-[#10B981]"
                : "cursor-not-allowed bg-[rgba(112,112,112,0.3)] text-[#2F363F]"
            }`}
          >
            Submit
          </Button>
        </div>
      </form>
    </>
  );
};

export default StepKYC;
